import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { publicApi } from '@/lib/api';
import { enrichHours, formatHoursSummary } from '@/lib/hours';
import { MapPin, Phone, Clock, Navigation, Bird } from 'lucide-react';
import { useLanguage } from '@/lib/i18n';

export default function Footer() {
  const [hoursSummary, setHoursSummary] = useState(''); // FIX-MOYENNE-01
  const [settings, setSettings] = useState({});
  const [version, setVersion] = useState('dev');
  const { t } = useLanguage();

  useEffect(() => {
    publicApi.getHours()
      .then(r => {
        if (r.data?.hours && r.data.hours.length > 0) { // FIX-MOYENNE-01
          setHoursSummary(formatHoursSummary(r.data.hours));
        }
      })
      .catch(() => {});
    publicApi.getSettings()
      .then(r => setSettings(r.data || {}))
      .catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/public/git-version`)
      .then(res => res.json())
      .then(data => {
        if (data.version) setVersion(data.version);
      })
      .catch(() => {});
  }, []);

  const reservationsEnabled = settings.reservationsModuleEnabled === true;
  const defaultNavLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/carte', label: t('footer.ourMenu') },
    { href: '/galerie', label: t('nav.gallery') },
    { href: '/avis', label: t('footer.customerReviews') },
    ...(reservationsEnabled ? [{ href: '/reservation', label: t('nav.reservation') }] : []),
    { href: '/mentions-legales', label: t('footer.legalNotice') },
  ];
  const navLinks = Array.isArray(settings.footer_links) && settings.footer_links.length > 0
    ? settings.footer_links
    : defaultNavLinks;

  return (
    <footer className="bg-garden-800 text-white relative overflow-hidden" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            {settings.restaurant_logo_url ? (
              <img
                src={settings.restaurant_logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${settings.restaurant_logo_url}` : settings.restaurant_logo_url}
                alt={settings.name || 'Cote Terrasse'}
                className="h-12 w-auto object-contain mb-4"
                data-testid="footer-logo-img"
              />
            ) : (
              <h3 className="font-accent text-3xl text-sunny mb-4 flex items-center gap-2">
                <Bird className="fill-sunny/20" />
                {settings.name || 'Cote Terrasse'}
              </h3>
            )}
            <p className="text-garden-300 text-sm leading-relaxed">{settings.footer_description || t('footer.description')}</p>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">{t('footer.navigation')}</h4>
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link key={link.href} to={link.href} className="text-garden-300 hover:text-sunny transition-colors text-sm">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">{t('footer.contact')}</h4>
            <div className="flex flex-col gap-3 text-sm">
              {settings.address && ( // FIX-HAUTE-01
                <a href={settings.maps_url || "https://www.google.com/maps"} target="_blank" rel="noreferrer" className="flex items-start gap-2 text-garden-300 hover:text-sunny transition-colors">
                  <MapPin size={16} className="mt-0.5 shrink-0" />
                  <span>{settings.address}</span>
                </a>
              )}
              {settings.phone && ( // FIX-HAUTE-01
                <a href={`tel:${settings.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-garden-300 hover:text-sunny transition-colors">
                  <Phone size={16} className="shrink-0" />
                  <span>{settings.phone}</span>
                </a>
              )}
              <div className="flex items-start gap-2 text-garden-300">
                <Navigation size={16} className="mt-0.5 shrink-0" />
                <div className="flex flex-col">
              {settings.access_info ? (
                settings.access_info.split('\n').map((line, i) => (
                  <span key={i} className="text-sm text-garden-300">{line}</span>
                ))
              ) : (
                <>
                  <span>Accès : Tram A (arrêt à proximité)</span>
                  <span>Bus 9 (arrêt à proximité)</span>
                </>
              )}
                </div>
              </div>
              {hoursSummary && ( // FIX-MOYENNE-01
                <div className="flex flex-col gap-1 text-garden-300" data-testid="footer-hours">
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="shrink-0" />
                    <span>{hoursSummary}</span>
                  </div>
                  {settings.kitchen_opening_time && (
                    <div className="text-sunny text-xs font-semibold pl-6">Cuisine dès {settings.kitchen_opening_time}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-garden-700 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-garden-400 text-xs">&copy; {new Date().getFullYear()} Cote Terrasse. {t('footer.copyright')}{settings.footer_show_version === true && version && <span className="ml-2 font-mono font-bold text-[10px] text-sunny/90"> - V{version}</span>}</p>
          <p className="text-garden-400/80 text-xs">Prix nets exprimés en Euros TTC. Conformément au décret n° 2015-447, les informations sur les allergènes présents dans nos plats sont disponibles sur simple demande auprès de notre équipe.</p>
          <div className="flex items-center gap-4">
            <Link to="/mentions-legales" className="text-garden-400 text-xs hover:text-sunny transition-colors">
              {t('footer.legalAndPrivacy')}
            </Link>
            <Link to="/admin" className="text-garden-600/40 text-[10px] hover:text-garden-400 transition-colors" data-testid="footer-admin-link">
              {t('footer.admin')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
